/**
 * RTK Query API endpoints for Thesys C1Chat.
 * 
 * Note: This API handles streaming responses from the backend.
 * The C1Chat component expects Server-Sent Events (SSE) format.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { THESYS_CHAT_DEFAULT_API_URL } from '../constants/thesysChatConstants';
import { carBaseQuery } from '@app/api/carBaseQuery';

export interface ThesysChatMessageRequest {
  prompt: {
    role: 'user' | 'assistant' | 'system';
    content: string;
    id?: string;
  };
  threadId: string;
  responseId: string;
  useCustomMarkdown?: boolean;
}

/**
 * Thesys Chat API.
 * 
 * Note: The sendMessage endpoint returns a ReadableStream for streaming responses.
 * The C1Chat component handles the streaming internally.
 */
export const thesysChatApi = createApi({
  reducerPath: 'thesysChatApi',
  baseQuery: carBaseQuery, // Use existing base query for auth
  endpoints: (builder) => ({
    sendMessage: builder.mutation<Response, ThesysChatMessageRequest>({
      query: (body) => ({
        url: THESYS_CHAT_DEFAULT_API_URL,
        method: 'POST',
        body,
        responseHandler: async (response) => {
          // Return the response object directly for streaming
          // C1Chat will handle reading the stream
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response;
        },
      }),
    }),
  }),
});

export const { useSendMessageMutation } = thesysChatApi;
